<template>
  <div class="attachment-stream-overlay" v-if="visible">
    <template v-if="!mobile">
      <div class="attachment-stream-hover-area">
        <!-- 閉じるボタン -->
        <div class="close-button" @click="close">
          <icon-photostream-close-button class="x" width="42" height="42" />
        </div>
        <!-- 左右ナビゲーション -->
        <button v-if="files.length > 1 && currentIndex > 0" class="nav-button left" @click="prev">
          <svg width="42" height="42" viewBox="0 0 42 42">
            <rect x="0" y="0" rx="8" width="42" height="42" fill="black" fill-opacity="0.7" />
            <polyline
              points="26,12 16,21 26,30"
              fill="none"
              stroke="white"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          v-if="files.length > 1 && currentIndex < files.length - 1"
          class="nav-button right"
          @click="next"
        >
          <svg width="42" height="42" viewBox="0 0 42 42">
            <rect x="0" y="0" rx="8" width="42" height="42" fill="black" fill-opacity="0.7" />
            <polyline
              points="16,12 26,21 16,30"
              fill="none"
              stroke="white"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <!-- ファイル名ラベル（フッターより10px上・左寄り） -->
        <div v-if="image" class="filename-label-abs">
          <div class="filename-label-name">{{ getFileName(image) }}</div>
          <div v-if="size > 0" class="filename-label-size" :style="{ width: sizeLabelWidth }">
            {{ `(${size % 1000}KB)` }}
          </div>
        </div>
      </div>
      <!-- ファイル表示 -->
      <div :class="['attachment-stream-content']">
        <attachment-img
          ref="attachment"
          v-if="image"
          :image="image"
          :param="param"
          :photostream="true"
          :downloadable="downloadable(image)"
          :isMobile="isMobile"
          :allowSaveAttachments="allowSaveAttachments"
          :clientType="clientType"
          v-on:loadedImage="loadedImage"
        />
      </div>
      <!-- 下部黒帯とダウンロードボタン -->
      <div class="attachment-stream-footer">
        <div class="download-btn-wrapper" :id="'download-btn-wrapper-' + currentIndex">
          <button
            :id="'download-button-' + currentIndex"
            :class="{ 'download-btn': true, disabled: !downloadable(image) }"
            @click="downloadFile"
            :disabled="!downloadable(image)"
            tabindex="-1"
          >
            <b-icon-slash-circle v-if="!downloadable(image)" />
            <b-icon-download v-else /> {{ downloadButtonLabel }}
          </button>
          <b-tooltip
            :target="'download-btn-wrapper-' + currentIndex"
            triggers="hover focus"
            placement="top"
            :disabled="downloadable(image)"
          >
            {{ notDownloadNotes }}
          </b-tooltip>
        </div>
      </div>
    </template>
    <!-- モバイル版UI -->
    <template v-else>
      <button class="nav-button option" @click="toggleDropdown" ref="optionBtn">
        <svg width="42" height="42" viewBox="0 0 42 42">
          <rect x="0" y="0" rx="8" width="42" height="42" fill="black" fill-opacity="0.7" />
          <circle cx="14" cy="21" r="2" fill="white" />
          <circle cx="21" cy="21" r="2" fill="white" />
          <circle cx="28" cy="21" r="2" fill="white" />
        </svg>
      </button>
      <div v-if="showDropdown" class="dropdown-menu-option custom-dropdown" :style="dropdownStyle">
        <button
          class="dropdown-item"
          :class="{ disabled: !downloadable(image) }"
          @click="downloadFile"
        >
          <span v-if="!downloadable(image)"><b-icon-slash-circle class="mx-1" /></span
          >{{ downloadButtonLabel }}
        </button>
      </div>

      <div class="close-button" @click="close">
        <icon-photostream-close-button class="x" width="42" height="42" />
      </div>

      <!-- ファイル名ラベル -->
      <div v-if="image" class="filename-label-abs">
        <div class="filename-label-name mobile-filename-label">{{ getFileName(image) }}</div>
        <div v-if="size > 0" class="filename-label-size" :style="{ width: sizeLabelWidth }">
          {{ `(${size % 1000}KB)` }}
        </div>
      </div>
      <!-- ファイル表示 -->
      <div :class="['attachment-stream-content']">
        <b-modal id="not-download" hide-header hide-footer static centered>
          <template #default="{ ok }">
            <div class="text-center">
              <p class="not-download-alert-label">{{ notDownloadNotes }}</p>
              <cancel :label="notDownloadAlertCloseLabel" v-on:cancel="ok" />
            </div>
          </template>
        </b-modal>
        <attachment-img
          ref="attachment"
          v-if="image"
          :image="image"
          :param="param"
          :photostream="true"
          :downloadable="downloadable(image)"
          :isMobile="isMobile"
          :allowSaveAttachments="allowSaveAttachments"
          :clientType="clientType"
          v-on:loadedImage="loadedImage"
        />
      </div>
      <!-- モバイル用: フッターに左右ナビゲーションのみ配置（黒帯なし） -->
      <div class="attachment-stream-footer-mobile">
        <button v-if="files.length > 1 && currentIndex > 0" class="nav-button left" @click="prev">
          <svg width="42" height="42" viewBox="0 0 42 42">
            <rect x="0" y="0" rx="8" width="42" height="42" fill="black" fill-opacity="0.7" />
            <polyline
              points="26,12 16,21 26,30"
              fill="none"
              stroke="white"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <button
          v-if="files.length > 1 && currentIndex < files.length - 1"
          class="nav-button right"
          @click="next"
        >
          <svg width="42" height="42" viewBox="0 0 42 42">
            <rect x="0" y="0" rx="8" width="42" height="42" fill="black" fill-opacity="0.7" />
            <polyline
              points="16,12 26,21 16,30"
              fill="none"
              stroke="white"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useDomainStore, useUserStore } from "@/store";
import AttachmentImg from "../AttachmentImg.vue";
import IconPhotostreamCloseButton from "../icon/IconPhotostreamCloseButton.vue";
import type { Attachment } from "../../model";
import { User } from "../../model";
import { CustomFileType } from "@/domain-setting-types";
import { getFileType } from "../file-utility";
import type { ClientType } from "@/store/domain-store";
import type { DownloadDeviceTypes } from "@/suppport-attachment-types";
import S3AccessUtility from "../s3-acceess-utility";
import Cancel from "../button/Cancel.vue";

const DOWNLOAD_BUTTON_LABEL = "ダウンロード";
const NOT_DOWNLOAD_NOTES = "この組織では端末へのファイル保存が制限されています。";
const NOT_DOWNLOAD_ALERT_CLOSE_LABEL = "閉じる";

export default defineComponent({
  components: {
    AttachmentImg,
    IconPhotostreamCloseButton,
    Cancel,
  },
  props: {
    files: { type: Array as PropType<Attachment[]>, default: () => [] },
    param: {
      type: Object as PropType<{ messageId: string; commentId: string }>,
      // NOTE: 旧コードはオブジェクトリテラル直書きの default（Vue 2 では dev 警告対象）
      // だったが、eslint(vue/require-valid-default-prop) 対応で factory 化。
      // param は読み取り専用のため挙動は変わらない。
      default: () => ({ messageId: "", commentId: "" }),
    },
    isMobile: { type: Boolean, default: false },
    clientType: {
      type: [String, Boolean] as PropType<ClientType>,
      default: false as unknown as ClientType,
      required: false,
    },
    allowSaveAttachments: {
      type: Object as PropType<DownloadDeviceTypes>,
      default: () => ({ web: true, ios: true, android: true, desktop: true }),
      required: false,
    },
    startIndex: { type: Number, default: 0 },
    visible: { type: Boolean, default: true },
  },
  setup(props, { emit }) {
    // $bvModal は BootstrapVue が生やす Vue 2 インスタンス専用 API のため
    // getCurrentInstance 経由で参照する
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const currentIndex = ref<number>(-1); // 初期値を-1として、他のファイルを該当させない
    const size = ref<number>(0);
    const showDropdown = ref<boolean>(false);
    const dropdownStyle = ref<Record<string, string | number>>({});

    // 旧 this.$refs.attachment / this.$refs.optionBtn 用の template ref
    const attachment = ref<InstanceType<typeof AttachmentImg>>();
    const optionBtn = ref<HTMLElement>();

    const mobile = computed((): boolean => {
      return props.isMobile;
    });

    const downloadButtonLabel = computed((): string => {
      return DOWNLOAD_BUTTON_LABEL;
    });

    const notDownloadNotes = computed((): string => {
      return NOT_DOWNLOAD_NOTES;
    });

    const notDownloadAlertCloseLabel = computed((): string => {
      return NOT_DOWNLOAD_ALERT_CLOSE_LABEL;
    });

    const sizeLabelWidth = computed((): string => {
      const k = 1024;
      const units = ["B", "KB", "MB"];
      const i = Math.floor(Math.log(size.value) / Math.log(k));
      // ローカル変数名は ref の size と衝突するため sizeLabel に変更（ロジックは旧コードのまま）
      const sizeLabel = parseFloat((size.value / Math.pow(k, i)).toFixed(0));
      const unit = units[i];
      return `(${sizeLabel} ${unit})`;
    });

    const downloadableList = computed((): string[] => {
      // ユーザーのroleを見て、ダウンロード可能かどうかを判断後 downloadTypeList を返す
      const domainId = useAppStore().domainId;
      const me = useUserStore().me as User | undefined;
      if (me && me.isDownloadableRole(domainId)) {
        return useDomainStore().getDownloadTypeList;
      } else {
        return [];
      }
    });

    const image = computed((): Attachment | undefined => {
      const image = props.files[currentIndex.value];
      return image;
    });

    // ファイル名を取得
    const getFileName = (file: Attachment | undefined): string => {
      return S3AccessUtility.decodeName(file?.url || "");
    };

    // ...ボタンを押したときにドロップダウンの開閉を切り替える
    const toggleDropdown = (e: MouseEvent) => {
      showDropdown.value = !showDropdown.value;
      nextTick(() => {
        if (showDropdown.value) {
          const btn = optionBtn.value;
          if (btn) {
            const rect = btn.getBoundingClientRect();
            dropdownStyle.value = {
              position: "absolute",
              top: rect.bottom + "px",
              right: window.innerWidth - rect.right + "px",
              left: "auto",
              zIndex: 3000,
            };
          }
        }
      });
      e.stopPropagation();
    };

    // ドロップダウン外をクリックしたときにドロップダウンを閉じる
    const handleClickOutside = (e: MouseEvent) => {
      const btn = optionBtn.value;
      const menu = document.querySelector(".custom-dropdown");
      if (
        showDropdown.value &&
        menu &&
        !menu.contains(e.target as Node) &&
        btn &&
        !btn.contains(e.target as Node)
      ) {
        showDropdown.value = false;
      }
    };

    // ファイルがダウンロード可能か判定
    const downloadable = (file: Attachment | undefined): boolean => {
      if (!file) return false;
      const type = getFileType(file);
      let customFileType: CustomFileType = "image";
      switch (type) {
        case "image":
          customFileType = "image";
          break;
        case "video":
          customFileType = "movie";
          break;
        case "pdf":
          customFileType = "pdf";
          break;
        case "office":
          customFileType = "office";
          break;
        default:
          break;
      }
      return downloadableList.value.includes(customFileType);
    };

    // モバイルでのピンチイン/アウトを制御
    const controlUserScale = (allow: boolean): void => {
      const viewportContent = `width=device-width,initial-scale=1.0${!allow ? ",user-scalable=no" : ""}`;
      document.querySelector("meta[name='viewport']")?.setAttribute("content", viewportContent);
    };

    // 画像の時のみピンチイン/アウトを許可
    const controlScaleForImage = () => {
      if (image.value && getFileType(image.value) === "image") {
        controlUserScale(true);
      } else {
        controlUserScale(false);
      }
    };

    // 前のファイルに移動
    const prev = () => {
      if (currentIndex.value > 0) {
        clearAttachmentPath();
        currentIndex.value--;
        controlScaleForImage();
      }
    };
    // 次のファイルに移動
    const next = () => {
      if (currentIndex.value < props.files.length - 1) {
        clearAttachmentPath();
        currentIndex.value++;
        controlScaleForImage();
      }
    };
    // ストリームを閉じる
    const close = () => {
      emit("close");
      controlUserScale(false);
    };

    // 画像のロード時にサイズをセット
    // （引数名は ref の size と衝突するため newSize に変更。ロジックは旧コードのまま）
    const loadedImage = (newSize: number): void => {
      // サイズをセット
      size.value = newSize;
    };

    // ファイルダウンロード
    const downloadFile = async (): Promise<void> => {
      if (image.value) {
        if (!downloadable(image.value)) {
          vm?.$bvModal.show("not-download");
        } else {
          const s3AccessUtility = S3AccessUtility.getInstance();
          const fileName = getFileName(image.value);
          await s3AccessUtility.downloadFile(
            image.value.url,
            props.param,
            image.value.mime,
            fileName
          );
        }
      }
    };

    // AttachmentImgのpathをクリア
    const clearAttachmentPath = (): void => {
      // AttachmentImg はクラス→defineComponent 化したため InstanceType で参照する
      const attachmentImg = attachment.value;
      if (attachmentImg) {
        attachmentImg.clearPath();
      }
    };

    // マウント時の初期化処理
    onMounted(() => {
      currentIndex.value = props.startIndex;
      controlScaleForImage();
      document.body.style.overflow = "hidden";
      document.addEventListener("click", handleClickOutside);
    });

    // コンポーネント破棄時のクリーンアップ
    onBeforeUnmount(() => {
      document.body.style.overflow = "";
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      currentIndex,
      size,
      showDropdown,
      dropdownStyle,
      attachment,
      optionBtn,
      mobile,
      downloadButtonLabel,
      notDownloadNotes,
      notDownloadAlertCloseLabel,
      sizeLabelWidth,
      downloadableList,
      image,
      getFileName,
      toggleDropdown,
      handleClickOutside,
      downloadable,
      controlUserScale,
      controlScaleForImage,
      prev,
      next,
      close,
      loadedImage,
      downloadFile,
      clearAttachmentPath,
    };
  },
});
</script>

<style lang="scss" scoped>
.download-btn {
  background: #2e2e2e;
  color: #fff;
  border: 1px solid #fff;
  border-radius: 8px;
  font-size: 1.1rem;
  padding: 10px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition:
    background 0.2s,
    color 0.2s,
    border 0.2s;

  &.disabled {
    color: rgba(255, 255, 255, 0.65) !important;
    border: 1px solid rgba(255, 255, 255, 0.65) !important;
    svg {
      color: rgba(255, 255, 255, 0.65) !important;
    }
  }
}
.attachment-stream-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 58px;
  background: #2e2e2e;
  z-index: 2200;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-right: 32px;
}

.attachment-stream-footer-mobile {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: 58px;
  background: transparent;
  z-index: 2200;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
}

.filename-label-abs {
  position: fixed;
  left: 0;
  bottom: 68px; /* フッター高さ58px+10px */
  background: #2e2e2e;
  color: #fff;
  font-size: 1.05rem;
  padding: 4px 16px;
  max-width: 100vw;
  white-space: normal;
  word-break: break-all;
  overflow-wrap: break-word;
  pointer-events: none;
  z-index: 2300;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 12px;

  .filename-label-name {
    color: #fff;
    width: 100%;
    word-break: break-all;
    overflow-wrap: break-word;
  }
  .mobile-filename-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 90vw;
  }
  .filename-label-size {
    color: #999999;
    margin-left: 4px;
    min-width: 4em;
    max-width: 12em;
    text-align: left;
    white-space: nowrap;
  }
}

.download-btn:hover {
  background: #444;
  color: #fff;
  border: 1px solid #fff;
}
.download-btn.disabled,
.download-btn:disabled {
  cursor: not-allowed !important;
}
.download-btn.disabled:hover,
.download-btn:disabled:hover {
  background: #2e2e2e !important;
  color: rgba(255, 255, 255, 0.65) !important;
  border: 1px solid rgba(255, 255, 255, 0.65) !important;
}
.attachment-stream-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  user-select: none;
  caret-color: transparent;
}
.attachment-stream-content {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.mobile-header-flex {
  width: 100%;
  top: 24px;
  display: flex;
  position: relative;
}
.close-button {
  position: absolute;
  top: 24px;
  right: 32px;
  width: 42px;
  height: 42px;
  cursor: pointer;
  z-index: 2100;
  .x {
    width: 42px;
    height: 42px;
    stroke: white;
    fill: black;
  }
}
.nav-button {
  background: #000;
  border: none;
  padding: 0;
  width: 42px;
  height: 42px;
  border-radius: 20px;
  cursor: pointer;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.2s;
  svg {
    rect {
      fill: #2e2e2e;
      fill-opacity: 1;
    }
    polyline {
      stroke: white;
      fill: none;
    }
  }
}
.nav-button.left {
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  left: 32px;
}
.nav-button.right {
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  right: 32px;
}
.nav-button:disabled {
  position: absolute;
  opacity: 0.3;
  cursor: default;
  background: #222;
}
.nav-button:hover:not(:disabled) {
  background: #222;
}
.nav-button.option {
  position: absolute !important;
  padding: 0 !important;
  top: 24px;
  right: 80px; // closeボタン(32px) + ボタン幅(42px) + 6px間隔
  width: 42px;
  height: 42px;
  z-index: 2100;
}
.dropdown-menu-option {
  position: absolute !important;
  z-index: 2100 !important;
}
.attachment-stream-hover-area {
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 58px; /* フッター高さ分 */
  opacity: 0;
  transition: opacity 0.2s;
}
.attachment-stream-overlay:hover .attachment-stream-hover-area {
  opacity: 1;
  pointer-events: auto;
}
.b-tooltip,
.popover.b-tooltip {
  z-index: 2100;
}
.not-download-alert-label {
  font-size: 16px;
  font-weight: bold;
  color: #212529;
}
.custom-dropdown {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 120px;
  position: absolute;
}
.custom-dropdown .dropdown-item {
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  padding: 8px 16px;
  font-size: 1rem;
  color: #222;
}
.custom-dropdown .dropdown-item:disabled {
  color: #aaa;
}
.custom-dropdown .dropdown-item.disabled {
  color: #aaa;
  pointer-events: none;
}
</style>
